import React, { useCallback, useEffect, useMemo, useState } from 'react';
import apiClient, { permissionMessage } from '../config/axiosConfig';
import './AdminPanel.css';
import './SuperAdminUsers.css';

interface Location {
  id: number;
  name: string;
  city: string;
}

interface UserLocation {
  id: number;
  name: string | null;
  city: string | null;
  receive_emails: boolean;
  /** 'zelf gekozen' of het e-mailadres van de superadmin die dit koppelde. */
  assigned_by: string | null;
  assigned_at: string;
  self_chosen: boolean;
}

interface BeheerderUser {
  id: number;
  email: string;
  name: string | null;
  is_active: boolean;
  has_logged_in: boolean;
  is_superadmin: boolean;
  locations: UserLocation[];
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
  message?: string;
}

interface SuperAdminUsersProps {
  locations: Location[];
  onClose: () => void;
}

const formatDate = (isoString: string): string => {
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return isoString;
  return date.toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const SuperAdminUsers: React.FC<SuperAdminUsersProps> = ({ locations, onClose }) => {
  const [users, setUsers] = useState<BeheerderUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [savingIds, setSavingIds] = useState<number[]>([]);
  const [confirmDelete, setConfirmDelete] = useState<BeheerderUser | null>(null);

  const [showAddForm, setShowAddForm] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newLocationIds, setNewLocationIds] = useState<number[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiClient.get<ApiResponse<BeheerderUser[]>>('/admin/users');
      setUsers(response.data.data || []);
    } catch (err) {
      setError(permissionMessage(err) || 'De beheerders konden niet geladen worden.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return users;
    return users.filter(
      (user) =>
        user.email.toLowerCase().includes(query) || (user.name || '').toLowerCase().includes(query)
    );
  }, [users, search]);

  const markSaving = (id: number, saving: boolean) =>
    setSavingIds((current) => (saving ? [...current, id] : current.filter((item) => item !== id)));

  const toggleLocation = async (user: BeheerderUser, locationId: number) => {
    markSaving(user.id, true);
    setError(null);
    setSuccess(null);

    const currentIds = user.locations.map((location) => location.id);
    const nextIds = currentIds.includes(locationId)
      ? currentIds.filter((id) => id !== locationId)
      : [...currentIds, locationId];

    try {
      const response = await apiClient.put<ApiResponse<BeheerderUser>>(`/admin/users/${user.id}`, {
        location_ids: nextIds,
      });
      setUsers((current) =>
        current.map((item) => (item.id === user.id ? response.data.data : item))
      );
      setSuccess(`Locaties van ${user.name || user.email} bijgewerkt.`);
    } catch (err) {
      const apiError = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(permissionMessage(err) || apiError || 'De wijziging kon niet opgeslagen worden.');
    } finally {
      markSaving(user.id, false);
    }
  };

  const toggleActive = async (user: BeheerderUser) => {
    markSaving(user.id, true);
    setError(null);
    setSuccess(null);

    try {
      const response = await apiClient.put<ApiResponse<BeheerderUser>>(`/admin/users/${user.id}`, {
        is_active: !user.is_active,
      });
      setUsers((current) =>
        current.map((item) => (item.id === user.id ? response.data.data : item))
      );
      setSuccess(
        user.is_active
          ? `${user.name || user.email} is op inactief gezet en kan niet meer inloggen.`
          : `${user.name || user.email} kan weer inloggen.`
      );
    } catch (err) {
      const apiError = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(permissionMessage(err) || apiError || 'De wijziging kon niet opgeslagen worden.');
    } finally {
      markSaving(user.id, false);
    }
  };

  const addUser = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!newEmail.trim()) return;

    setIsSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await apiClient.post<ApiResponse<BeheerderUser>>('/admin/users', {
        email: newEmail.trim(),
        name: newName.trim() || undefined,
        location_ids: newLocationIds,
      });
      setNewEmail('');
      setNewName('');
      setNewLocationIds([]);
      setShowAddForm(false);
      setSuccess(response.data.message || 'Beheerder toegevoegd.');
      await fetchUsers();
    } catch (err) {
      const apiError = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(permissionMessage(err) || apiError || 'De beheerder kon niet toegevoegd worden.');
    } finally {
      setIsSaving(false);
    }
  };

  const deleteUser = async () => {
    if (!confirmDelete) return;

    markSaving(confirmDelete.id, true);
    setError(null);
    setSuccess(null);

    try {
      await apiClient.delete(`/admin/users/${confirmDelete.id}`);
      setSuccess(`${confirmDelete.name || confirmDelete.email} is verwijderd.`);
      setConfirmDelete(null);
      await fetchUsers();
    } catch (err) {
      const apiError = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(permissionMessage(err) || apiError || 'De beheerder kon niet verwijderd worden.');
    } finally {
      markSaving(confirmDelete.id, false);
    }
  };

  return (
    <div className="admin-panel">
      <div className="admin-container">
        <div className="admin-header">
          <div className="header-left">
            <button type="button" className="back-to-locations-btn" onClick={onClose}>
              ← Terug
            </button>
            <div className="location-info">
              <h1>Beheerders en locaties</h1>
            </div>
          </div>
        </div>

        <div className="tab-content superadmin-users">
          <p className="superadmin-intro">
            Iedereen die met een Microsoft-account van IJsselheem kan inloggen, kiest bij de eerste
            keer zelf een locatie. Die keuze zie je hieronder terug. Alleen jij kunt iemand naar een
            andere locatie verplaatsen, aan een extra locatie koppelen of het account uitzetten.
          </p>

      {error && <div className="superadmin-message superadmin-message-error">{error}</div>}
      {success && <div className="superadmin-message superadmin-message-success">{success}</div>}

      <div className="superadmin-toolbar">
        <input
          type="search"
          className="superadmin-search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Zoek op naam of e-mailadres..."
          aria-label="Zoek een beheerder"
        />
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setShowAddForm((current) => !current)}
        >
          {showAddForm ? 'Annuleren' : '+ Beheerder toevoegen'}
        </button>
      </div>

      {showAddForm && (
        <form className="superadmin-form" onSubmit={addUser}>
          <div className="superadmin-form-row">
            <div className="superadmin-form-group">
              <label htmlFor="new-user-email">E-mailadres</label>
              <input
                id="new-user-email"
                type="email"
                value={newEmail}
                onChange={(event) => setNewEmail(event.target.value)}
                placeholder="naam@ijsselheem.nl"
                required
                disabled={isSaving}
              />
            </div>
            <div className="superadmin-form-group">
              <label htmlFor="new-user-name">Naam (optioneel)</label>
              <input
                id="new-user-name"
                type="text"
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
                placeholder="Voor- en achternaam"
                disabled={isSaving}
              />
            </div>
          </div>

          <fieldset className="superadmin-locations-field">
            <legend>Locaties die deze persoon mag beheren</legend>
            <div className="superadmin-location-options">
              {locations.map((location) => (
                <label key={location.id} className="superadmin-location-option">
                  <input
                    type="checkbox"
                    checked={newLocationIds.includes(location.id)}
                    onChange={(event) =>
                      setNewLocationIds((current) =>
                        event.target.checked
                          ? [...current, location.id]
                          : current.filter((id) => id !== location.id)
                      )
                    }
                    disabled={isSaving}
                  />
                  <span>
                    {location.name}
                    {location.city ? ` · ${location.city}` : ''}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <button type="submit" className="btn btn-primary" disabled={isSaving || !newEmail.trim()}>
            {isSaving ? 'Bezig...' : 'Beheerder toevoegen'}
          </button>
        </form>
      )}

      {isLoading ? (
        <p className="superadmin-loading">Beheerders laden...</p>
      ) : filteredUsers.length === 0 ? (
        <div className="superadmin-empty">
          {users.length === 0
            ? 'Er zijn nog geen beheerders toegevoegd.'
            : 'Geen beheerder gevonden met deze zoekopdracht.'}
        </div>
      ) : (
        <ul className="superadmin-list">
          {filteredUsers.map((user) => {
            const isBusy = savingIds.includes(user.id);
            const userLocationIds = user.locations.map((location) => location.id);
            const selfChosen = user.locations.filter((location) => location.self_chosen);

            return (
              <li key={user.id} className={`superadmin-card ${user.is_active ? '' : 'is-inactive'}`}>
                <div className="superadmin-card-top">
                  <div className="superadmin-identity">
                    <span className="superadmin-name">
                      {user.name || user.email}
                      {user.is_superadmin && <span className="superadmin-tag tag-super">superadmin</span>}
                      {!user.has_logged_in && (
                        <span className="superadmin-tag tag-pending">nog niet ingelogd</span>
                      )}
                      {selfChosen.length > 0 && (
                        <span className="superadmin-tag tag-self">zelf aangemeld</span>
                      )}
                      {!user.is_active && <span className="superadmin-tag tag-off">inactief</span>}
                    </span>
                    {user.name && <span className="superadmin-email">{user.email}</span>}
                    {selfChosen.map((location) => (
                      <span key={location.id} className="superadmin-claim">
                        Koos zelf {location.name || 'onbekende locatie'} op{' '}
                        {formatDate(location.assigned_at)}
                      </span>
                    ))}
                  </div>

                  <div className="superadmin-card-actions">
                    {!user.is_superadmin && (
                      <>
                        <button
                          type="button"
                          className="btn btn-quiet"
                          onClick={() => toggleActive(user)}
                          disabled={isBusy}
                        >
                          {user.is_active ? 'Op inactief zetten' : 'Weer activeren'}
                        </button>
                        <button
                          type="button"
                          className="superadmin-remove"
                          onClick={() => setConfirmDelete(user)}
                          disabled={isBusy}
                          title={`${user.name || user.email} verwijderen`}
                          aria-label={`${user.name || user.email} verwijderen`}
                        >
                          ✕
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div className="superadmin-location-grid">
                  {locations.length === 0 ? (
                    <span className="superadmin-no-locations">Er zijn nog geen locaties.</span>
                  ) : (
                    locations.map((location) => (
                      <label key={location.id} className="superadmin-location-option">
                        <input
                          type="checkbox"
                          checked={userLocationIds.includes(location.id)}
                          onChange={() => toggleLocation(user, location.id)}
                          disabled={isBusy}
                        />
                        <span>{location.name}</span>
                      </label>
                    ))
                  )}
                </div>

                {user.is_superadmin && (
                  <p className="superadmin-note">
                    Deze persoon staat in de serverinstelling SUPERADMIN_EMAILS en mag daardoor
                    altijd alles, ongeacht de vinkjes hierboven.
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {confirmDelete && (
        <div className="modal-overlay">
          <div className="modal-content superadmin-confirm">
            <h3>Beheerder verwijderen</h3>
            <p>
              Weet je zeker dat je <strong>{confirmDelete.name || confirmDelete.email}</strong> wilt
              verwijderen?
            </p>
            <p className="superadmin-confirm-hint">
              Deze persoon verliest de toegang tot alle locaties en ontvangt geen e-mails meer. Let
              op: wie in Entra nog mag inloggen, kan daarna opnieuw een locatie kiezen. Wil je de
              toegang echt blokkeren, zet het account dan op inactief in plaats van te verwijderen.
            </p>
            <div className="superadmin-confirm-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setConfirmDelete(null)}>
                Annuleren
              </button>
              <button type="button" className="btn btn-danger" onClick={deleteUser}>
                Ja, verwijderen
              </button>
            </div>
          </div>
        </div>
      )}
        </div>
      </div>
    </div>
  );
};

export default SuperAdminUsers;
